import {
  ButtonList,
  DropdownButton,
  DropdownButtonContext,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { Satisfaction } from 'lib-shared-legacy-types';
import { useMemo } from 'react';

import {
  getCSATLanguages,
  SupportedCSATLanguageCode,
} from '../../../../../utils/supportedCSATLanguages/supportedCSATLanguages';

import styles from './surveyLanguagesDropdown.module.scss';

/**
 * Unsupported languages specifically for phone text-to-speach (IVR) settings,
 * that need to be filtered out.
 */
const unsupportedIVRLanguages: SupportedCSATLanguageCode[] = [
  'et', // Estonian
  'fa', // Persian
  'hr', // Croatian
  'sl', // Slovenian
  'sq', // Albanian
];

type SurveyLanguageDropdownProps = {
  surveyType?: Satisfaction.SurveyType;
  languageSettings: Satisfaction.CsatSettings[];
  handleGoToLanguage: (lang: string) => void;
};

export const SurveyLanguageDropdown = ({
  surveyType,
  languageSettings,
  handleGoToLanguage,
}: SurveyLanguageDropdownProps): JSX.Element => {
  const supportedCSATLanguages = useMemo(() => getCSATLanguages(), []);

  const availableLanguages = supportedCSATLanguages.filter(({ code }) => {
    const hasLanguageAlready = languageSettings.some((settings) => {
      return settings.language === code;
    });

    if (hasLanguageAlready) {
      return false;
    }

    if (surveyType === 'text_to_speech') {
      return !unsupportedIVRLanguages.includes(code);
    }

    return true;
  });

  const hasNoLanguagesToChoose = availableLanguages.length === 0;

  return (
    <Tooltip
      content={hasNoLanguagesToChoose ? 'No more languages available' : null}
    >
      <DropdownButton
        noArrow
        fixed
        icon="plus"
        buttonProps={{ variant: 'secondary', disabled: hasNoLanguagesToChoose }}
        dropdownProps={{ className: styles.languagesDropdown }}
        dropdown={
          <DropdownButtonContext.Consumer>
            {() => (
              <ButtonList>
                {availableLanguages.map((language) => (
                  <ButtonList.Item
                    key={language.code}
                    onClick={() => handleGoToLanguage(language.code)}
                  >
                    {language.label}
                  </ButtonList.Item>
                ))}
              </ButtonList>
            )}
          </DropdownButtonContext.Consumer>
        }
      >
        Add language
      </DropdownButton>
    </Tooltip>
  );
};
